import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from 'react';
import { usePut } from '../hooks';
import { StoreData } from '@/types/store';
import { DayOfWeek, OpeningTimes } from '@/types/common';
import { ChannelType, PauseConfig } from '@/types/channels';
import { OpeningHoursUpdateResponse } from '@/types/api';
import { useStore } from './StoreContext';
import {
  buildBulkOpeningHoursPayload,
  buildClearTemporaryHoursPayload,
  buildCompleteOpeningHoursPayload,
  buildGroupOpeningHoursPayload,
} from '@/utils/storeNormalizer';

// Context state interface
interface ChannelsContextState {
  store: StoreData | null;
  pauseLoading: boolean;
  pauseError: string | null;
  unpauseLoading: boolean;
  unpauseError: string | null;
  openingHoursLoading: boolean;
  openingHoursError: string | null;
  updatePauseChannel: (
    channels: string[],
    pause: PauseConfig,
    onComplete?: (success: boolean, error?: string) => void
  ) => void;
  unpauseChannel: (
    channels: string[],
    onComplete?: (success: boolean, error?: string) => void
  ) => void;
  updateChannelOpeningHours: (
    channel: string | string[],
    days: DayOfWeek[],
    type: 'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES',
    openingHours?: OpeningTimes[],
    onComplete?: (success: boolean, error?: string) => void,
    expiresAt?: string,
    inheritedHours?: Partial<Record<DayOfWeek, OpeningTimes[]>>
  ) => void;
  updateGroupOpeningHours: (
    channelGroup: ChannelType,
    days: DayOfWeek[],
    type: 'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES',
    openingHours?: OpeningTimes[],
    overwriteOverriddenChannels?: boolean,
    onComplete?: (
      success: boolean,
      error?: string,
      skippedChannels?: string[],
      failedChannels?: string[]
    ) => void,
    expiresAt?: string
  ) => void;
  resetChannelsToGroupHours: (
    channels: string[],
    onComplete?: (success: boolean, error?: string) => void
  ) => void;
  endTemporaryOpeningHours: (
    channel: string,
    onComplete?: (success: boolean, error?: string) => void
  ) => void;
}

// Context provider props - store can be null during initial load
interface ChannelsProviderProps {
  children: ReactNode;
  store: StoreData | null;
  restaurantId: number | null;
}

// Create the context with default values
const ChannelsContext = createContext<ChannelsContextState | undefined>(
  undefined
);

// Context Provider Component
export const ChannelsProvider: React.FC<ChannelsProviderProps> = ({
  children,
  store,
  restaurantId,
}) => {
  const { refetchChannels } = useStore();

  // Separate usePut hooks for pause and opening hours to avoid race conditions
  const {
    refetch: updatePauseStore,
    loading: pauseLoading,
    error: pauseError,
  } = usePut<{ [key: string]: unknown }>(null, undefined, {}, false, true);

  const {
    refetch: updateUnpauseStore,
    loading: unpauseLoading,
    error: unpauseError,
  } = usePut<{ [key: string]: unknown }>(null, undefined, {}, false, true);

  const {
    data: openingHoursData,
    refetch: updateOpeningHoursStore,
    loading: openingHoursLoading,
    error: openingHoursError,
  } = usePut<OpeningHoursUpdateResponse>(null, undefined, {}, false, true);

  // Separate refs for each operation to prevent callback cross-talk
  const pauseOnCompleteRef = useRef<
    ((success: boolean, error?: string) => void) | null
  >(null);
  const pausePreviousLoadingRef = useRef<boolean>(false);

  const unpauseOnCompleteRef = useRef<
    ((success: boolean, error?: string) => void) | null
  >(null);
  const unpausePreviousLoadingRef = useRef<boolean>(false);

  const openingHoursOnCompleteRef = useRef<
    | ((
        success: boolean,
        error?: string,
        skippedChannels?: string[],
        failedChannels?: string[]
      ) => void)
    | null
  >(null);
  const openingHoursPreviousLoadingRef = useRef<boolean>(false);
  // A group update writes each channel on its own downstream, so one channel failing does not undo
  // the others: it is reported as a successful operation rather than a failed update.
  const openingHoursGroupUpdateRef = useRef<boolean>(false);

  // Effect for pause channel completion
  useEffect(() => {
    if (pausePreviousLoadingRef.current && !pauseLoading) {
      if (pauseOnCompleteRef.current) {
        if (pauseError) {
          pauseOnCompleteRef.current(false, pauseError);
        } else {
          pauseOnCompleteRef.current(true);
          refetchChannels();
        }
        pauseOnCompleteRef.current = null;
      }
    }
    pausePreviousLoadingRef.current = pauseLoading;
  }, [pauseLoading, pauseError, refetchChannels]);

  // Effect for unpause channel completion
  useEffect(() => {
    if (unpausePreviousLoadingRef.current && !unpauseLoading) {
      if (unpauseOnCompleteRef.current) {
        if (unpauseError) {
          unpauseOnCompleteRef.current(false, unpauseError);
        } else {
          unpauseOnCompleteRef.current(true);
          refetchChannels();
        }
        unpauseOnCompleteRef.current = null;
      }
    }
    unpausePreviousLoadingRef.current = unpauseLoading;
  }, [unpauseLoading, unpauseError, refetchChannels]);

  // Effect for opening hours completion - refetch channel data whenever the backend responded
  useEffect(() => {
    if (openingHoursPreviousLoadingRef.current && !openingHoursLoading) {
      if (openingHoursOnCompleteRef.current) {
        if (openingHoursError) {
          openingHoursOnCompleteRef.current(false, openingHoursError);
        } else if (openingHoursData) {
          // Check response for failed updates
          const failedResults =
            openingHoursData.results?.filter(
              result => result.status === 'UPDATE_FAILED' || result.error
            ) || [];

          if (failedResults.length > 0 && !openingHoursGroupUpdateRef.current) {
            // Build error message from failed results
            const errorMessages = failedResults
              .map(r => r.error || `${r.channel}: ${r.status}`)
              .join(', ');
            openingHoursOnCompleteRef.current(false, errorMessages);
          } else {
            openingHoursOnCompleteRef.current(
              true,
              undefined,
              openingHoursData.skippedChannels,
              openingHoursGroupUpdateRef.current
                ? failedResults
                    .map(result => result.channel)
                    .filter((channel): channel is string => !!channel?.trim())
                : undefined
            );
          }
          // The channels the backend did write must show up without a manual reload, so the
          // refetch follows a response with per-channel failures too.
          refetchChannels();
        } else {
          openingHoursOnCompleteRef.current(true);
          // Refetch channel data to get updated opening hours (clears cache and loads new hours)
          refetchChannels();
        }
        openingHoursOnCompleteRef.current = null;
      }
    }
    openingHoursPreviousLoadingRef.current = openingHoursLoading;
  }, [
    openingHoursLoading,
    openingHoursError,
    openingHoursData,
    refetchChannels,
  ]);

  const unpauseChannel = (
    channels: string[],
    onComplete?: (success: boolean, error?: string) => void
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }
    if (onComplete) {
      unpauseOnCompleteRef.current = onComplete;
    }
    updateUnpauseStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/unpause`,
      {
        channels: channels,
      }
    );
  };

  const updatePauseChannel = (
    channels: string[],
    pauseConfig: PauseConfig,
    onComplete?: (success: boolean, error?: string) => void
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }

    if (!pauseConfig) {
      console.warn('Pause config is not available');
      return;
    }

    if (onComplete) {
      pauseOnCompleteRef.current = onComplete;
    }
    // Store is guaranteed to exist, no undefined checks needed
    updatePauseStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/pause`,
      {
        channels: channels,
        ...pauseConfig,
      }
    );
  };

  const updateChannelOpeningHours = (
    channel: string | string[],
    days: DayOfWeek[],
    type: 'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES',
    openingHours?: OpeningTimes[],
    onComplete?: (success: boolean, error?: string) => void,
    expiresAt?: string,
    inheritedHours?: Partial<Record<DayOfWeek, OpeningTimes[]>>
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }

    const body = Array.isArray(channel)
      ? buildBulkOpeningHoursPayload(channel, days, type, openingHours)
      : buildCompleteOpeningHoursPayload(
          channel,
          days,
          type,
          openingHours,
          store?.channels?.[channel],
          expiresAt,
          inheritedHours
        );

    if (onComplete) {
      openingHoursOnCompleteRef.current = onComplete;
    }
    openingHoursGroupUpdateRef.current = false;
    updateOpeningHoursStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/opening-hours`,
      body
    );
  };

  const updateGroupOpeningHours = (
    channelGroup: ChannelType,
    days: DayOfWeek[],
    type: 'OPEN_ALL_DAY' | 'CLOSED_ALL_DAY' | 'OPEN_BETWEEN_TIMES',
    openingHours?: OpeningTimes[],
    overwriteOverriddenChannels?: boolean,
    onComplete?: (
      success: boolean,
      error?: string,
      skippedChannels?: string[],
      failedChannels?: string[]
    ) => void,
    expiresAt?: string
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }

    if (onComplete) {
      openingHoursOnCompleteRef.current = onComplete;
    }
    openingHoursGroupUpdateRef.current = true;
    updateOpeningHoursStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/opening-hours`,
      buildGroupOpeningHoursPayload(
        channelGroup,
        days,
        type,
        openingHours,
        overwriteOverriddenChannels,
        expiresAt
      )
    );
  };

  const resetChannelsToGroupHours = (
    channels: string[],
    onComplete?: (success: boolean, error?: string) => void
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }

    if (onComplete) {
      openingHoursOnCompleteRef.current = onComplete;
    }
    openingHoursGroupUpdateRef.current = false;
    updateOpeningHoursStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/opening-hours/reset`,
      { channels: channels.map(channel => channel.toUpperCase()) }
    );
  };

  const endTemporaryOpeningHours = (
    channel: string,
    onComplete?: (success: boolean, error?: string) => void
  ) => {
    if (!restaurantId) {
      console.warn('Restaurant ID is not available');
      return;
    }

    if (onComplete) {
      openingHoursOnCompleteRef.current = onComplete;
    }
    openingHoursGroupUpdateRef.current = false;
    updateOpeningHoursStore(
      process.env.REACT_APP_API_DOMAIN +
        `stores/${restaurantId}/channels/opening-hours`,
      buildClearTemporaryHoursPayload(channel)
    );
  };

  // Context value
  const contextValue: ChannelsContextState = {
    store,
    pauseLoading: pauseLoading || false,
    pauseError: pauseError || null,
    unpauseLoading: unpauseLoading || false,
    unpauseError: unpauseError || null,
    openingHoursLoading: openingHoursLoading || false,
    openingHoursError: openingHoursError || null,
    updatePauseChannel,
    unpauseChannel,
    updateChannelOpeningHours,
    updateGroupOpeningHours,
    resetChannelsToGroupHours,
    endTemporaryOpeningHours,
  };

  return (
    <ChannelsContext.Provider value={contextValue}>
      {children}
    </ChannelsContext.Provider>
  );
};

// Custom hook to use the Channels context
export const useChannels = (): ChannelsContextState => {
  const context = useContext(ChannelsContext);

  if (context === undefined) {
    throw new Error('useChannels must be used within a ChannelsProvider');
  }

  return context;
};

// Export the context for advanced use cases (optional)
export { ChannelsContext };
